import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Container,
    Typography,
    Paper,
    Grid,
    Autocomplete,
    TextField,
    Button,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Chip
} from '@mui/material';
import UpdateIcon from '@mui/icons-material/Update';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGrid } from '@mui/x-data-grid';
import { toast } from 'react-toastify';

import LoadingBox from '../../utils/LoadingBox';
import {
    getDefaultTransferRates,
    updateDefaultTransferRates,
    getTransferSeasons
} from './RatesSlice';
import { getModels } from '../fleet/FleetSlice';

const formatDayMonth = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    return parts.length === 3 ? `${parts[2]}-${parts[1]}` : dateStr;
};

export default function DefaultTransferRates() {
    const { isLoading, defaultTransferRates, transferSeasons } = useSelector((state) => state.rates);
    const { models } = useSelector((state) => state.fleet);
    const { user } = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    const isSuperUser = Boolean(user?.is_superuser);

    const [filterModel, setFilterModel] = useState(null);
    const [filterSeason, setFilterSeason] = useState(null);
    const [openBulkUpdate, setOpenBulkUpdate] = useState(false);
    const [defaultRate, setDefaultRate] = useState('0.00');
    const [percentChange, setPercentChange] = useState('0.00');
    const [absChange, setAbsChange] = useState('0.00');

    // Local edited rows for spreadsheet-like inline editing
    const [gridRows, setGridRows] = useState([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
        dispatch(getDefaultTransferRates());
        dispatch(getTransferSeasons());
        dispatch(getModels());
    }, [dispatch]);

    useEffect(() => {
        if (defaultTransferRates?.rows) {
            const mappedRows = defaultTransferRates.rows.map((r) => {
                const rowObj = {
                    id: r.id,
                    vehicle: `${r.vehicle_make} ${r.vehicle_model}`,
                    vehicle_make: r.vehicle_make,
                    vehicle_model: r.vehicle_model,
                };
                if (defaultTransferRates.seasons) {
                    defaultTransferRates.seasons.forEach((s) => {
                        rowObj[`season_${s.id}`] = r.rates?.[`season_${s.id}`] ?? '0.00';
                    });
                }
                return rowObj;
            });
            setGridRows(mappedRows);
            setHasUnsavedChanges(false);
        }
    }, [defaultTransferRates]);

    const seasons = (defaultTransferRates?.seasons && defaultTransferRates.seasons.length > 0)
        ? defaultTransferRates.seasons
        : (transferSeasons || []);

    const visibleSeasons = filterSeason ? seasons.filter((s) => s.id === filterSeason.id) : seasons;

    // Filter rows
    const displayRows = gridRows.filter((row) => {
        if (filterModel && row.id !== filterModel.id) return false;
        return true;
    });

    // Build columns with clear season header labels
    const columns = [
        {
            field: 'vehicle',
            headerName: 'Vehicle Model',
            width: 220,
            renderCell: (params) => (
                <strong>{params.value}</strong>
            )
        },
        ...visibleSeasons.map((s) => ({
            field: `season_${s.id}`,
            headerName: `${s.name} (${formatDayMonth(s.start_date)} ~ ${formatDayMonth(s.end_date)})`,
            width: 170,
            editable: isSuperUser,
            type: 'number',
            headerAlign: 'center',
            align: 'right',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', width: '100%', py: 0.5, lineHeight: 1.2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.825rem', color: '#1e293b' }}>
                        {s.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.72rem' }}>
                        {formatDayMonth(s.start_date)} ~ {formatDayMonth(s.end_date)}
                    </Typography>
                </Box>
            ),
            valueGetter: (params) => {
                const val = params.row[`season_${s.id}`];
                return val !== undefined && val !== null ? parseFloat(val).toFixed(2) : '0.00';
            },
            valueSetter: (params) => {
                if (!isSuperUser) return params.row;
                const parsed = parseFloat(params.value);
                const newVal = !isNaN(parsed) && parsed >= 0 ? parsed.toFixed(2) : '0.00';
                return { ...params.row, [`season_${s.id}`]: newVal };
            }
        }))
    ];

    const processRowUpdate = (newRow) => {
        if (!isSuperUser) return newRow;
        setGridRows((prevRows) =>
            prevRows.map((row) => (row.id === newRow.id ? { ...row, ...newRow } : row))
        );
        setHasUnsavedChanges(true);
        return newRow;
    };

    const handleSaveMatrix = () => {
        if (!isSuperUser) return;
        const payloadRows = gridRows.map((r) => {
            const ratesObj = {};
            seasons.forEach((s) => {
                ratesObj[`season_${s.id}`] = r[`season_${s.id}`] ?? '0.00';
            });
            return {
                id: r.id,
                rates: ratesObj
            };
        });

        dispatch(updateDefaultTransferRates({ rows: payloadRows }))
            .unwrap()
            .then(() => {
                setHasUnsavedChanges(false);
            });
    };

    const handleBulkUpdateSubmit = (e) => {
        e.preventDefault();
        if (!isSuperUser) return;
        const parsedDef = parseFloat(defaultRate);
        const parsedPct = parseFloat(percentChange);
        const parsedAbs = parseFloat(absChange);

        if (isNaN(parsedDef) || parsedDef < 0) {
            toast.error('Base rate must be a positive number');
            return;
        }
        if (isNaN(parsedPct) || parsedPct < -1 || parsedPct > 10) {
            toast.error('Percent change must be between -1.00 and 10.00');
            return;
        }
        if (isNaN(parsedAbs)) {
            toast.error('Absolute change must be a valid number');
            return;
        }

        const updateData = {
            model: filterModel ? { id: filterModel.id } : null,
            season: filterSeason ? { id: filterSeason.id } : null,
            default_rate: parsedDef.toFixed(2),
            percent_change: parsedPct.toFixed(2),
            abs_change: parsedAbs.toFixed(2)
        };

        dispatch(updateDefaultTransferRates(updateData))
            .unwrap()
            .then(() => {
                setOpenBulkUpdate(false);
            });
    };

    return (
        <Container maxWidth="xl">
            <Typography variant="h3" align="center" gutterBottom>
                Default Transfer rates
            </Typography>

            {/* Filter and Action Bar */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4} md={3}>
                    <Autocomplete
                        value={filterModel}
                        onChange={(e, val) => setFilterModel(val)}
                        options={models && Array.isArray(models) ? models : []}
                        getOptionLabel={(opt) => opt ? `${opt.vehicle_make} ${opt.vehicle_model}` : ''}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        renderInput={(params) => <TextField {...params} label="Filter Model" size="small" />}
                    />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                    <Autocomplete
                        value={filterSeason}
                        onChange={(e, val) => setFilterSeason(val)}
                        options={seasons}
                        getOptionLabel={(opt) => opt ? `${opt.name} (${opt.start_date})` : ''}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        renderInput={(params) => <TextField {...params} label="Filter Season" size="small" />}
                    />
                </Grid>
                <Grid item xs={12} sm={4} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => dispatch(getDefaultTransferRates())}
                        size="medium"
                    >
                        Refresh
                    </Button>
                    {isSuperUser ? (
                        <>
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<UpdateIcon />}
                                onClick={() => setOpenBulkUpdate(true)}
                                size="medium"
                            >
                                Bulk / Formula Update
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SaveIcon />}
                                onClick={handleSaveMatrix}
                                disabled={!hasUnsavedChanges}
                                size="medium"
                            >
                                Save Matrix Changes
                            </Button>
                        </>
                    ) : (
                        <Chip label="Platform Default Rates (View Only)" variant="outlined" color="info" size="medium" />
                    )}
                </Grid>
            </Grid>

            {isSuperUser && hasUnsavedChanges && (
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Chip label="Unsaved changes in grid" color="warning" size="small" />
                </Box>
            )}

            <Paper sx={{ p: 2, width: '100%', overflowX: 'auto' }}>
                <LoadingBox isLoading={isLoading} elements={gridRows} noneText="No transfer rates or eligible models found" />
                <DataGrid
                    rows={displayRows}
                    columns={columns}
                    columnHeaderHeight={52}
                    rowHeight={44}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={(error) => toast.error(error.message)}
                    autoHeight
                    disableRowSelectionOnClick
                    initialState={{
                        pagination: { paginationModel: { page: 0, pageSize: 25 } }
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    sx={{
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f1f5f9',
                            borderBottom: '2px solid #cbd5e1'
                        },
                        '& .MuiDataGrid-columnHeader': {
                            backgroundColor: '#f1f5f9',
                            fontWeight: 700,
                            fontSize: '0.8125rem',
                            borderRight: '1px solid #e2e8f0'
                        },
                        '& .MuiDataGrid-cell--editable': {
                            cursor: isSuperUser ? 'pointer' : 'default',
                            '&:hover': isSuperUser ? {
                                backgroundColor: 'rgba(25, 118, 210, 0.06)'
                            } : {}
                        }
                    }}
                />
            </Paper>

            {/* Bulk / Formula Update Dialog (SuperUser Only) */}
            {isSuperUser && (
                <Dialog open={openBulkUpdate} onClose={() => setOpenBulkUpdate(false)} maxWidth="sm" fullWidth>
                    <form onSubmit={handleBulkUpdateSubmit}>
                        <DialogTitle>Bulk / Formula Update Transfer Rates</DialogTitle>
                        <DialogContent>
                            <DialogContentText sx={{ mb: 2 }}>
                                Apply a base rate or percentage / absolute change to transfer rates. Formula applied:
                                <br />
                                <strong>New Rate = (Base Rate × (1 + Percent)) + Absolute</strong>
                            </DialogContentText>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Base Rate"
                                        type="number"
                                        value={defaultRate}
                                        onChange={(e) => setDefaultRate(e.target.value)}
                                        helperText="0 keeps current rate as base"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Percent Change"
                                        type="number"
                                        inputProps={{ step: '0.01' }}
                                        value={percentChange}
                                        onChange={(e) => setPercentChange(e.target.value)}
                                        helperText="e.g. 0.10 for +10%"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Absolute Change"
                                        type="number"
                                        inputProps={{ step: '0.01' }}
                                        value={absChange}
                                        onChange={(e) => setAbsChange(e.target.value)}
                                        helperText="Fixed amount to add/sub"
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Target Scope:
                                    <strong> Model:</strong> {filterModel ? `${filterModel.vehicle_make} ${filterModel.vehicle_model}` : 'All Transfer Models'} |
                                    <strong> Season:</strong> {filterSeason ? filterSeason.name : 'All Active Seasons'}
                                </Typography>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenBulkUpdate(false)} color="secondary">Cancel</Button>
                            <Button type="submit" variant="contained" color="primary">Apply Updates</Button>
                        </DialogActions>
                    </form>
                </Dialog>
            )}
        </Container>
    );
}
